import publicRuntimeConfig from "@FRAMEWORK/config/public";
import {oauth2TokenAsync} from "@QUERIES/account/useOauth2Token";
import {device} from "@yb/utils";
import {NextRouter} from "next/router";
import React from "react";

/** 각 값은 중복 없이 유일하게 포함됨 */
type TokenScope = "openid" | "address" | "profile";

export type YBTourTokenPayload = {
	sub: string;
	aud: string;
	nbf: number;
	scope: TokenScope[];
	iss: string;
	exp: number;
	iat: number;
	jti: string;
	device: string;
	name?: string;
	nonUser: boolean;
};

/**
 * JWT Token을 디코딩하여 payload 객체 반환
 * 서명 검증은 하지 않음 (디버깅/프론트 사용용)
 */
export const decodeJwt = (token: string): YBTourTokenPayload | null => {
	try {
		const [, payload] = token.split(".");
		if (!payload) return null;
		const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
		return JSON.parse(decoded);
	} catch (err) {
		console.error("Invalid JWT", err);
		return null;
	}
};

/** bottom bar 여부 */
export const isBottomFn = (router: NextRouter) =>
	!(!!router.query?.$partnerName || /gmarket|product\/(detailPackage|(reserve(?!Complete)))|pub/.test(router.pathname));

/** app scheme bottom bar 처리 */
export const appBottomScheme = (isBottom: boolean) => {
	const isApp = device.isYBApp();
	if (!isApp) return;
	const visible = isBottom ? "Y" : "N";
	location.href = `ybtour://setNavigationBar?visible=${visible}`;
};

export const getTarget = (target: string | "SELF" | "NEW" | "IFRAME"): "_self" | "_blank" => {
	const targetType = {
		SELF: "_self",
		NEW: "_blank",
		IFRAME: "_self",
		_self: "_self",
		_blank: "_blank",
	};
	return targetType[target] || "_self";
};

export const getCookie = (name: string) => {
	const cookieString = document.cookie;
	const cookies = cookieString.split("; ");
	for (let i = 0; i < cookies.length; i++) {
		const cookie = cookies[i].split("=");
		if (cookie[0] === name) {
			return cookie[1];
		}
	}
	return null;
};

export const getQueryParams = (search: string = window.location.search): Record<string, string> => {
	const queryString = search.startsWith("http") ? new URL(search).search : search;
	return Object.fromEntries(new URLSearchParams(queryString).entries());
};

/** 해피톡 실행함수 */
export const openTalk = (url: string) => {
	if (!url) return;
	if (device.isYBApp()) {
		location.href = `ybtour://openBrowser?url=${encodeURIComponent(url)}`;
	} else {
		const nWidth = 500;
		const nHeight = 800;
		let xPos = document.body.clientWidth / 2 - nWidth / 2;
		xPos += window.screenLeft;
		const yPos = screen.availHeight / 2 - nHeight / 2;
		window.open(
			url,
			"talk",
			`toolbar=no,scrollbars=no,resizable=yes,top=${yPos},left=${xPos},width=${nWidth},height=${nHeight},`,
		);
	}
};

/** 채널 브릿지 (계정관리) */
export const getSSOUrl = async (url: string) => {
	try {
		const currentOrigin = location.origin;
		const targetUrl = new URL(url);
		const isSameDomain = targetUrl.origin === currentOrigin;
		// 타겟 도메인과 현재 도메인이 같은 경우
		if (isSameDomain) return url;
		// ybtour 도메인이 아닌 경우
		if (!targetUrl.host.includes("ybtour")) return url;

		const {data} = await oauth2TokenAsync();
		// 로그인 상태 아닌경우
		if (data.code !== "0000") return url;
		// 비회원 로그인 상태인 경우
		if (data.body.access_token) {
			const payload = decodeJwt(data.body.access_token);
			if (payload?.nonUser) return url;
		}
		// 로그인 상태인 경우
		const ssoUrl = new URL(`${publicRuntimeConfig.ENV_ACCOUNTS_DOMAIN}/sso`);

		let channelCode = "main001";
		if (targetUrl.host.includes("mpkg")) channelCode = "pkg002";
		else if (targetUrl.host.includes("pkg")) channelCode = "pkg001";
		else if (targetUrl.host.includes("prdt")) channelCode = "prdt001";
		else if (targetUrl.host.includes("travel")) channelCode = "travel001";
		else if (targetUrl.host.includes("fly")) channelCode = "air001";
		else if (targetUrl.host.includes("ota")) channelCode = "ota001";
		else if (targetUrl.host.includes("fit")) channelCode = "fit001";
		else if (targetUrl.host.includes("accounts")) channelCode = "account001";
		else if (targetUrl.host.includes("support")) channelCode = "support001";

		const token = data.body.access_token;
		const returnUrl = targetUrl.toString();

		ssoUrl.searchParams.set("channelCode", channelCode);
		ssoUrl.searchParams.set("token", token);
		ssoUrl.searchParams.set("returnUrl", returnUrl);
		return ssoUrl.toString();
	} catch (error) {
		return url;
	}
};

export const onClickSSO = (
	e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
	url: string,
	target: string | "_blank" | "_self" = "_self",
) => {
	e.preventDefault();
	getSSOUrl(url).then(href => window.open(href, target));
};

export const hrefSSO = (url: string, target: string | "_blank" | "_self" = "_self") => {
	getSSOUrl(url).then(href => window.open(href, target));
};

export const replaceSSO = (url: string) => {
	getSSOUrl(url).then(href => window.location.replace(href));
};
